import React, { useState, useEffect } from 'react';
import KickImage from 'Reuseables/KickImage';
import { connect } from 'react-redux';
import { IconContext } from 'react-icons';
import { FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import moment from 'moment';
// ========= action import section ========= //
import {
  doFetchProfile,
  doSentLinkVerificationEmail,
  getOtp,
  resetOtpState,
  getOtpStatus,
} from '../../../actions/authAction';
// ====== end of action import section ====== //
import { printError } from '../../../utils';
// ========= interface import section ========= //
import AuthInterface, {
  UserDataInterface,
} from '../../../interface/reducers/auth';
import ModalChangePasswordV3 from '../../Modals/ModalChangePasswordV3';
import { ModalChangePasswordV4, ModalOtp } from '../../Modals';
import { doChangePassword } from '../../../actions/resetPasswordAction';
// ====== end of interface import section ====== //

/**
 * Custom Hooks
 */
import { useOtpStorage } from '../../../helpers/dom';

type SubmitChangePasswordPayload = {
  new_password: string;
  old_password: string;
  otp: string;
};

type stateToProps = {
  dataAuth: AuthInterface;
};

interface AccountInfoInterface {
  // state to props
  userData: UserDataInterface;

  // dispatch to props
  doVerifyEmail: () => Promise<any>;
  getOtp: () => Promise<any>;
  resetOtpState: () => Promise<any>;
  doFetchMyProfile: () => Promise<any>;
  doSubmitChangePassword: (
    payload: SubmitChangePasswordPayload,
  ) => Promise<any>;
}

/**
 * Account Info Tab on Setting User Dashboard section for Kick Avenue 3.0 Website.
 * ===============================================
 * @param {AccountInfoInterface} props for this component.
 * @return {JSX.Element}
 */
function AccountInfoTab({
  userData,
  doVerifyEmail,
  doFetchMyProfile,
  doSubmitChangePassword,
  getOtp,
  otpCode,
  changePassword,
  resetOtpState,
  getOtpStatus,
  otpStatus,
}: AccountInfoInterface): JSX.Element {
  const history = useHistory();
  const [isOtpActive, setIsOtpActive] = useState(false);

  const {
    saveToLocalStorage,
    checkIfExpired,
    resendOtp,
    resetOtpFromLocalstorage,
    saveResendOtpToLocalStorage,
  } = useOtpStorage();
  const [disabled, setDisabled] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const toggleModal = () => setOpenModal(!isOpenModal);

  const [showModalOtp, setShowModalOtp] = useState(false);
  const [passwordPayload, setPasswordPayload] = useState<
    SubmitChangePasswordPayload
  >({
    old_password: '',
    new_password: '',
    otp: '',
  });
  const [gettingOtp, setGettingOtp] = useState(false);
  const [otpErrorMsg, setOtpErrorMsg] = useState('');
  const [currentProcess, setCurrentProcess] = useState('');

  const onClickVerify = () => {
    setDisabled(true);
    doFetchMyProfile().then(() => {
      if (userData.roles.filter((t) => t === 'unverified_user').length) {
        doVerifyEmail().then(() => {
          setDisabled(false);
          toast.success(
            'Please check your email to continue the verification process.',
            {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: false,
              progress: undefined,
            },
          );
        });
      } else {
        setDisabled(false);
      }
    });
  };
  const onSubmitPasswordChange = (password) => {
    doSubmitChangePassword(password);
  };

  const onSubmitPasswordWithOtp = (otpCode) => {
    setGettingOtp(false);
    doSubmitChangePassword({ ...passwordPayload, otp: otpCode });
  };

  const getOtpHandler = () => {
    if (checkIfExpired()) {
      getOtp();
    } else {
      setShowModalOtp(true);
      setGettingOtp(false);
      setOpenModal(false);
      setOtpErrorMsg('');
    }
  };

  useEffect(() => {
    if (otpStatus?.data?.change_password) {
      setIsOtpActive(otpStatus.data.change_password);
    } else {
      setIsOtpActive(false);
    }
  }, [otpStatus?.data?.change_password]);

  useEffect(() => {
    if (otpCode.success) {
      setOtpErrorMsg('');
      saveToLocalStorage();
      saveResendOtpToLocalStorage();
      setShowModalOtp(true);
      setGettingOtp(false);
      setOpenModal(false);
    } else if (otpCode?.message) {
      setOtpErrorMsg(otpCode?.message);
      setShowModalOtp(true);
      setGettingOtp(false);
      setOpenModal(false);
    }
  }, [otpCode]);

  useEffect(() => {
    if (changePassword?.error) {
      const passwordError1 =
        'Your password is the same as one of your last 5 passwords. Please choose a different password.';
      const passwordError2 = 'The old password field is invalid.';

      let errorMsg = '';
      if (changePassword?.error?.errors) {
        const errors = Object.values(changePassword?.error?.errors);
        if (Array.isArray(errors) && errors.length > 0) {
          if (Array.isArray(errors[0]) && errors[0].length > 0) {
            errorMsg = errors[0][0];
          }
        }
      } else {
        errorMsg = changePassword?.error?.message ?? '';
      }

      setOtpErrorMsg(errorMsg);
      if (isOtpActive) {
        setGettingOtp(false);
      }

      if (errorMsg === passwordError1 || errorMsg === passwordError2) {
        setShowModalOtp(false);
        setOpenModal(true);
        resetOtpState();
        setCurrentProcess('');
      }
    } else if (changePassword.status_code === 200) {
      setOtpErrorMsg('');
      setCurrentProcess('password-changed');
      setOpenModal(false);
      resetOtpFromLocalstorage();
      setPasswordPayload({
        old_password: '',
        new_password: '',
        otp: '',
      });
    } else {
      setOtpErrorMsg('');
    }
  }, [changePassword]);

  useEffect(() => {
    setCurrentProcess('');
  }, [showModalOtp]);

  useEffect(() => {
    getOtpStatus();
  }, []);

  return (
    <div className={'account-info-container'}>
      <div className={'form-parent'}>
        <div className={'form-field'}>
          <p>Email</p>
          <input
            placeholder={'Enter your Email'}
            disabled
            defaultValue={userData.email}
          />
        </div>
        <div className={'button-field'}>
          {userData.roles.filter((t) => t === 'unverified_user').length ? (
            <div
              className={`button-green ${disabled ? 'disabled' : ''}`}
              onClick={onClickVerify}
            >
              <p>Verify</p>
            </div>
          ) : (
            <div className={'button-white'}>
              <p>Verified</p>
              <IconContext.Provider
                value={{ color: '#159953', size: '12.5px' }}
              >
                <div style={{ marginLeft: '8px' }}>
                  <FaCheck />
                </div>
              </IconContext.Provider>
            </div>
          )}
        </div>
      </div>
      <div className={'form-parent'}>
        <div className={'form-field'}>
          <p>Password</p>
          <input
            placeholder={'Enter your Password'}
            type={'password'}
            disabled
            value={'aaaaaaaaa'}
          />
        </div>
        <div className={'button-field'}>
          <div className={'button-green'} onClick={toggleModal}>
            <p>Change Password</p>
          </div>
        </div>
      </div>
      {/* <ModalChangePasswordV3
        titleModal="Change Password"
        isOpen={isOpenModal}
        onToggle={toggleModal}
        hasPassword={userData.have_password}
        submitPassword={onSubmitPasswordChange}
      /> */}
      {isOpenModal ? (
        <ModalChangePasswordV4
          titleModal="Change Password"
          show={isOpenModal}
          onToggle={toggleModal}
          hasPassword={userData.have_password}
          // submitPassword={onSubmitPasswordChange}
          submitPassword={(passwordPayload) => {
            if (!isOtpActive) {
              onSubmitPasswordChange(passwordPayload);
            } else {
              setCurrentProcess('');
              setPasswordPayload(passwordPayload);
              setGettingOtp(true);
              getOtpHandler();
            }
          }}
          forgotPassword={() => {
            history.push('/forgot-password');
          }}
          passwordPayload={passwordPayload}
          errorMsg={otpErrorMsg}
        />
      ) : null}
      {showModalOtp ? (
        <ModalOtp
          show={showModalOtp}
          onHide={() => {
            setShowModalOtp(false);
            resetOtpState();
            setOpenModal(false);
          }}
          email={userData?.email ?? ''}
          submitOtp={(otp) => {
            onSubmitPasswordWithOtp(otp);
            setGettingOtp(true);
          }}
          resendCode={() => {
            setGettingOtp(true);
            resendOtp();
            getOtp();
          }}
          gettingOtp={gettingOtp}
          errorMsg={otpErrorMsg}
          currentProcess={currentProcess}
          backAction={() => {
            setShowModalOtp(false);
            setOpenModal(true);
            resetOtpState();
          }}
        />
      ) : null}
    </div>
  );
}

const mapStateToProps = (state: stateToProps) => {
  return {
    userData: state.dataAuth.userdata,
    otpCode: state.dataAuth.otpCode,
    otpStatus: state.dataAuth.otpStatus,
    changePassword: state.dataResetPassword.changePassword,
  };
};

const mapDispatchToProps = (dispatch: (arg0: any) => any) => {
  return {
    doVerifyEmail: () => dispatch(doSentLinkVerificationEmail()),
    doFetchMyProfile: () => dispatch(doFetchProfile()),
    getOtp: () => dispatch(getOtp()),
    resetOtpState: () => dispatch(resetOtpState()),
    getOtpStatus: () => dispatch(getOtpStatus()),
    doSubmitChangePassword: (payload: SubmitChangePasswordPayload) =>
      dispatch(doChangePassword(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountInfoTab);
